
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import lodash from "lodash";
import defensiveCopy from "@/util/defensive-copy";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import UnitInput from "@/components/Reusable/CustomInputs/UnitInput/UnitInput.vue";
import NumberInput from "@/components/Reusable/CustomInputs/NumberInput.vue";
import DropdownInput from "@/components/Reusable/CustomInputs/DropdownInput.vue";
import ToggleInput from "@/components/Reusable/CustomInputs/ToggleInput.vue";
import ButtonBarInput from "@/components/Reusable/CustomInputs/ButtonBarInput/ButtonBarInput.vue";
import WPMediaInput from "@/components/Reusable/CustomInputs/MediaInput/WPMediaInput.vue";
import VideoInput from "@/components/Reusable/CustomInputs/VideoInput.vue";
import { LinkInputValue } from "@/types/link";
import { ButtonBarItem } from "@/types/controls";
import AlertBox from "@/components/Reusable/CustomInputs/AlertBox.vue";
import { getAllSlidersInTree } from "@/lib/link-actions/sliders";
import { configStore, documentStore } from "@/store/store";
import DynamicDataChooser from "@/components/Reusable/ControlsTree/DynamicData/DynamicDataChooser.vue";
import { DynamicFieldOutput } from "@/types/dynamic-data";
import DynamicInput from "@/components/Reusable/CustomInputs/DynamicInput.vue";
import InlineRepeater from "@/components/Reusable/CustomInputs/InlineRepeater.vue";
import ColorInput from "@/components/Reusable/CustomInputs/Color/ColorInput.vue";
import IconButton from "@/components/UI/IconButton.vue";
import Icon from "@/components/UI/Icon.vue";
import Dropdown from "@/components/Reusable/Dropdown/Dropdown.vue";

@Component({
  components: {
    Dropdown,
    Icon,
    IconButton,
    ColorInput,
    InlineRepeater,
    DynamicInput,
    VideoInput,
    WPMediaInput,
    ButtonBarInput,
    ToggleInput,
    TextInput,
    DropdownInput,
    NumberInput,
    UnitInput,
    AlertBox,
    DynamicDataChooser
  }
})
export default class LinkInputSettings extends Vue {
  @Prop()
  value!: LinkInputValue;

  @Prop()
  hideAdvanced!: boolean;

  get lightboxDropdownItems(): ButtonBarItem[] {
    return [
      { text: "Image", value: "image", icon: "ImageIcon" },
      { text: "Video", value: "video", icon: "VideoIcon" }
    ];
  }

  get typeDropdownItems() {
    const linkTypes = [
      { text: "URL", value: "url" },
      { text: "Lightbox", value: "lightbox" },
      { text: "Contact", value: "contact" }
    ];

    if (configStore.builderMode === "breakdance") {
      linkTypes.push({ text: "Action", value: "action" });
    }

    return linkTypes;
  }

  get contactDropdownItems() {
    return [
      { text: "Email", value: "email" },
      { text: "Tel", value: "tel" },
      { text: "SMS", value: "sms" },
      { text: "WhatsApp", value: "whatsapp" }
    ];
  }

  get actionDropdownItems() {
    return [
      { text: "Control Slider", value: "slider" },
      { text: "Popup", value: "popup" }
    ];
  }

  get sliderActionsTypeDropdownItems() {
    return [
      { text: "Next Slide", value: "next" },
      { text: "Previous Slide", value: "previous" },
      { text: "Go To Slide", value: "goto" }
    ];
  }

  get sliderActionSlidersDropdownItems() {
    return getAllSlidersInTree(
      documentStore.document.tree.root,
      documentStore.document.documentMeta.id
    ).map(sliderNode => {
      return {
        text: sliderNode.name,
        value: sliderNode.uniqueClassName
      };
    });
  }

  get contactOptions() {
    return this.value.contact || {};
  }

  get actionOptions() {
    return this.value.action || {};
  }

  get sliderOptions() {
    return this.value.action?.sliderOptions || {};
  }

  get popupOptions() {
    return this.value.action?.popupOptions || {};
  }

  get lightboxOptions() {
    return this.value.lightbox || {};
  }

  get needsMessage() {
    return ["email", "whatsapp"].includes(this.contactOptions.type || "");
  }

  get needsPhoneNumber() {
    return ["sms", "tel", "whatsapp"].includes(this.contactOptions.type || "");
  }

  get isDynamicImage() {
    return (
      this.lightboxOptions.imageDynamicMeta !== undefined &&
      this.lightboxOptions.imageDynamicMeta !== null
    );
  }

  get isDynamicVideo() {
    return (
      this.lightboxOptions.videoDynamicMeta !== undefined &&
      this.lightboxOptions.videoDynamicMeta !== null
    );
  }

  updateSlideindex(newValue: number | null) {
    if (typeof newValue !== "number") {
      this.sliderOptions.slideIndex = undefined;
      return;
    }

    this.update(newValue, "action.sliderOptions.slideIndex");
  }

  get popupActionItems() {
    return [
      { text: "Open", value: "open" },
      { text: "Close", value: "close" },
      { text: "Toggle", value: "toggle" }
    ];
  }

  update(newValue: unknown, path: string) {
    const value = defensiveCopy(this.value);
    lodash.set(value, path, newValue);
    this.emit(value);
  }

  onDynamicImageSelected(value: DynamicFieldOutput) {
    const newValue = defensiveCopy(this.value);
    lodash.set(newValue, "lightbox.image", value.shortcode);
    lodash.set(newValue, "lightbox.imageDynamicMeta", value);
    this.emit(newValue);
  }

  clearDynamicImage() {
    const newValue = defensiveCopy(this.value);
    lodash.set(newValue, "lightbox.image", undefined);
    lodash.set(newValue, "lightbox.imageDynamicMeta", undefined);
    this.emit(newValue);
  }

  onDynamicVideoSelected(value: DynamicFieldOutput) {
    const newValue = defensiveCopy(this.value);
    lodash.set(newValue, "lightbox.video", value.shortcode);
    lodash.set(newValue, "lightbox.videoDynamicMeta", value);
    this.emit(newValue);
  }

  clearDynamicVideo() {
    const newValue = defensiveCopy(this.value);
    lodash.set(newValue, "lightbox.video", undefined);
    lodash.set(newValue, "lightbox.videoDynamicMeta", undefined);
    this.emit(newValue);
  }

  updateAttributeAtIndex(
    val: string,
    repeaterIndex: number,
    property: "name" | "value"
  ) {
    const attributes = defensiveCopy(this.value.html?.attributes) ?? [];
    attributes[repeaterIndex][property] = val;

    this.update(attributes, "html.attributes");
  }

  emit(newValue: LinkInputValue) {
    this.$emit("input", newValue);
  }
}
